import type { FormFieldSchema } from './types';

/**
 * Parses the custom fields schema from JSON and ensures each field has a stable ID.
 */
export function mapSchemaToFields(schemaJson: string | null | undefined): FormFieldSchema[] {
  if (!schemaJson) return [];
  try {
    const parsed = JSON.parse(schemaJson);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((f): f is FormFieldSchema => !!f && typeof f.id === 'string')
      .map((f) => ({
        ...f,
        // Ensure name is present for data binding, fallback to id
        name: f.name || f.id,
      }));
  } catch {
    return [];
  }
}

/**
 * Gets default values for the form from the schema and source values.
 */
export function getCustomFieldDefaults(
  fields: FormFieldSchema[],
  sourceValues: Record<string, unknown>
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  // If sourceValues has a 'content' field that is a string, parse it first
  // to extract the custom field values stored within.
  let data = { ...sourceValues };
  if (typeof sourceValues.content === 'string' && sourceValues.content) {
    try {
      const parsedContent = JSON.parse(sourceValues.content);
      data = { ...data, ...parsedContent };
    } catch {
      // Not a JSON string or other error, ignore
    }
  }

  fields.forEach((field) => {
    const key = field.name || field.id;
    const value = data[key];

    if (field.isPresentational) return;

    if (field.type === 'checkbox') {
      defaults[key] = value === 'true' || value === true;
    } else if (field.type === 'file') {
      if (field.multiple) {
        try {
          const parsed = value ? (typeof value === 'string' ? JSON.parse(value) : value) : [];
          defaults[key] = Array.isArray(parsed) ? parsed.map(Number) : [];
        } catch {
          defaults[key] = [];
        }
      } else {
        defaults[key] = value ? Number(value) : null;
      }
    } else if (field.type === 'number') {
      defaults[key] = value !== undefined && value !== null && value !== '' ? Number(value) : '';
    } else if (field.type === 'select' && field.multiple) {
      try {
        defaults[key] = value ? (typeof value === 'string' ? JSON.parse(value) : value) : [];
      } catch {
        defaults[key] = [];
      }
    } else if (field.type === 'address') {
      try {
        const parsed = value ? (typeof value === 'string' ? JSON.parse(value) : value) : {};
        defaults[key] = parsed || {};
      } catch {
        defaults[key] = {};
      }
    } else {
      defaults[key] = value || field.defaultValue || '';
    }
  });
  return defaults;
}

/**
 * Transforms form values back into database-compatible strings.
 * Note: With Zod, much of this logic might eventually move into the submission pipeline.
 */
export function prepareCustomFieldSubmission(
  fields: FormFieldSchema[],
  formData: Record<string, unknown>
): Record<string, string> {
  const submission: Record<string, string> = {};
  fields.forEach((field) => {
    if (field.isPresentational) return;

    const key = field.name || field.id;
    const value = formData[key];

    if (field.type === 'checkbox') {
      submission[key] = value ? 'true' : 'false';
    } else if (field.type === 'file') {
      if (field.multiple) {
        const ids = Array.isArray(value) ? value : value ? [value] : [];
        submission[key] = ids.length > 0 ? JSON.stringify(ids.map(Number)) : '';
      } else {
        submission[key] = value ? String(value) : '';
      }
    } else if (field.type === 'number') {
      submission[key] = value !== undefined && value !== '' && value !== null ? String(value) : '';
    } else if (Array.isArray(value)) {
      submission[key] = JSON.stringify(value);
    } else if (typeof value === 'object' && value !== null) {
      submission[key] = JSON.stringify(value);
    } else {
      submission[key] = value !== undefined && value !== null ? String(value) : '';
    }
  });
  return submission;
}

/**
 * High-level helper to prepare data for Contact create/update mutations.
 * Groups custom fields into 'content' and maps them to 'filterSlots'.
 */
export function prepareContactSubmission(
  fields: FormFieldSchema[],
  formData: Record<string, unknown>
) {
  const submission = prepareCustomFieldSubmission(fields, formData);
  const result: Record<string, unknown> = {
    content: JSON.stringify(submission),
  };

  fields.forEach((field) => {
    if (field.slot?.startsWith('filterSlot')) {
      result[field.slot] = submission[field.name || field.id];
    }
  });

  return result;
}

/**
 * Extracts all file IDs from the form data based on the field definitions.
 * Useful for updating database associations in a single pass.
 */
export function extractFileAssociations(
  fields: FormFieldSchema[],
  formData: Record<string, unknown>
): number[] {
  const fileIds: number[] = [];
  fields.forEach((field) => {
    if (field.type !== 'file') return;

    const key = field.name || field.id;
    const value = formData[key];

    if (Array.isArray(value)) {
      value.forEach((v) => {
        const id = Number(v);
        if (!isNaN(id) && id > 0) fileIds.push(id);
      });
    } else {
      const id = Number(value);
      if (!isNaN(id) && id > 0) fileIds.push(id);
    }
  });
  // Unique IDs only
  return Array.from(new Set(fileIds));
}
